/**
 * PrePublishGateProvider — mounts the universal pre-publish metadata gate once
 * at the app root. It:
 *   1. installs the confirm handler consumed by `confirmPublish()` in the
 *      publish service layer,
 *   2. installs a synchronous best-effort brand-name resolver that reads the
 *      react-query brand-bible cache (rows 4/12/18/26),
 *   3. installs an async slug-collision checker that calls the backend
 *      collision endpoint (rule 34 — warn-but-allow, fail-open),
 *   4. warms that cache for the current tenant so brand rules can fire,
 *   5. renders the warn-and-confirm dialog.
 *
 * It NEVER blocks rendering — children always pass through.
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '@/stores/useTenantStore';
import { brandBibleKeys, useBrandBible } from '@/hooks/useBrandBible';
import { useAuth } from '@/contexts/AuthContext';
import type { BrandBible } from '@/types/tenant';
import { blogCreationService } from '@/services/blogCreationService';
import {
  registerPrePublishConfirm,
  registerBrandNameResolver,
  registerSlugCollisionChecker,
  type EditableFieldValues,
  type GateItem,
  type PublishConfirmationPayload,
  type PublishConfirmResult,
} from '@/lib/prePublishGate';
import { PrePublishWarningDialog } from '@/components/publishing/PrePublishWarningDialog';

interface DialogState {
  open: boolean;
  items: GateItem[];
  resolve: ((result: PublishConfirmResult) => void) | null;
}

const CLOSED: DialogState = { open: false, items: [], resolve: null };

export const PrePublishGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { user, session } = useAuth();
  const currentTenant = useTenantStore((state) => state.currentTenant);

  // Warm the brand-bible cache for the active tenant so the resolver below has
  // something to read synchronously at publish time (best-effort; harmless).
  useBrandBible(currentTenant);

  const [dialog, setDialog] = React.useState<DialogState>(CLOSED);
  // Keep the live resolver in a ref so the registered handler is stable.
  const resolveRef = React.useRef<((result: PublishConfirmResult) => void) | null>(null);

  const authIdentity = React.useMemo(
    () => user?.email || session?.user?.email || user?.id || session?.user?.id || 'unknown-user',
    [session?.user?.email, session?.user?.id, user?.email, user?.id],
  );

  // Install the confirm handler once.
  React.useEffect(() => {
    const unregister = registerPrePublishConfirm(
      (items) =>
        new Promise<PublishConfirmResult>((resolve) => {
          resolveRef.current = resolve;
          setDialog({ open: true, items, resolve });
        }),
    );
    return () => {
      unregister();
      // If we unmount mid-dialog, fail open so no publish hangs forever.
      if (resolveRef.current) {
        resolveRef.current({ proceed: true });
        resolveRef.current = null;
      }
    };
  }, []);

  // Install the synchronous brand-name resolver.
  React.useEffect(() => {
    const unregister = registerBrandNameResolver((tenantId) => {
      const tenant = tenantId || currentTenant;
      if (!tenant) return null;
      const cached = queryClient.getQueryData<BrandBible | null>(brandBibleKeys.detail(tenant));
      if (cached?.business_name) return cached.business_name;
      const stored = useTenantStore.getState().getBrandBible?.(tenant);
      return stored?.business_name ?? null;
    });
    return unregister;
  }, [queryClient, currentTenant]);

  // Install the async slug-collision checker (rule 34 — warn-but-allow).
  // The tenant is captured from the store at call time so the checker closure
  // stays stable across tenant switches. Fail-soft: the service method itself
  // catches errors and returns { collides: false }.
  React.useEffect(() => {
    const unregister = registerSlugCollisionChecker((slug, excludeContentId) => {
      const tenant = useTenantStore.getState().currentTenant;
      return blogCreationService.checkSlugCollision(slug, excludeContentId, tenant);
    });
    return unregister;
  }, []);

  const buildConfirmation = React.useCallback(
    (
      items: GateItem[],
      details?: { justification?: string; reviewer?: string },
    ): PublishConfirmationPayload => {
      const rules = items.flatMap((item) =>
        item.violations.map((violation) => ({
          id: violation.ruleId,
          tier: violation.tier ?? violation.severity,
          message: violation.message,
        })),
      );
      return {
        confirmed_by: authIdentity,
        confirmed_at: new Date().toISOString(),
        rules,
        ...(details?.justification ? { justification: details.justification } : {}),
        ...(details?.reviewer ? { reviewer: details.reviewer } : {}),
      };
    },
    [authIdentity],
  );

  const settle = React.useCallback((proceed: boolean, details?: { justification?: string; reviewer?: string }) => {
    const resolve = resolveRef.current;
    const items = dialog.items;
    resolveRef.current = null;
    setDialog(CLOSED);
    if (resolve) {
      resolve(
        proceed
          ? { proceed: true, confirmation: buildConfirmation(items, details) }
          : { proceed: false },
      );
    }
  }, [buildConfirmation, dialog.items]);

  // Wire PATCH for the FIX section — called when the reviewer confirms with
  // metadata edits. Fail-open: a PATCH rejection returns false so the dialog
  // shows an inline error without losing the typed text.
  const handlePatch = React.useCallback(
    async (contentId: string, fields: EditableFieldValues): Promise<boolean> => {
      try {
        await blogCreationService.patchContent(contentId, fields, currentTenant);
        return true;
      } catch {
        return false;
      }
    },
    [currentTenant],
  );

  // Wire human-verify resolution for the CONFIRM section — Verify/Flag post
  // through the existing resolveHumanVerifyItem service call.
  const handleResolveHumanVerify = React.useCallback(
    async (
      contentId: string,
      itemId: string,
      status: 'verified' | 'dismissed',
      note?: string,
    ): Promise<boolean> => {
      try {
        await blogCreationService.resolveHumanVerifyItem(
          contentId,
          {
            item_id: itemId,
            status,
            reviewer: authIdentity,
            ...(note ? { note } : {}),
          },
          currentTenant,
        );
        return true;
      } catch {
        return false;
      }
    },
    [authIdentity, currentTenant],
  );

  return (
    <>
      {children}
      <PrePublishWarningDialog
        open={dialog.open}
        items={dialog.items}
        defaultReviewer={authIdentity}
        onGoBack={() => settle(false)}
        onPublishAnyway={(details) => settle(true, details)}
        onPatch={handlePatch}
        onResolveHumanVerify={handleResolveHumanVerify}
        tenantId={currentTenant}
      />
    </>
  );
};

export default PrePublishGateProvider;
